/**
 * Client Messages Hooks
 * React Query hooks for client-side message handling
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';

// Query keys
export const clientMessagesKeys = {
  all: ['client-messages'],
  messages: () => [...clientMessagesKeys.all, 'messages'],
  message: (id) => [...clientMessagesKeys.messages(), id],
};

/**
 * Get messages for the current client
 * This fetches messages where the client is the recipient
 */
export function useClientMessages() {
  return useQuery({
    queryKey: clientMessagesKeys.messages(),
    queryFn: async () => {
      // Get messages for the current user (client perspective)
      const response = await apiClient.get('/messages');
      return response;
    },
  });
}

/**
 * Get a single message by ID
 */
export function useClientMessage(id) {
  return useQuery({
    queryKey: clientMessagesKeys.message(id),
    queryFn: async () => {
      const response = await apiClient.get(`/messages/${id}`);
      return response;
    },
    enabled: !!id,
  });
}

/**
 * Client responds to a message
 */
export function useRespondToMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageId, responseData }) => {
      const response = await apiClient.post(`/messages/${messageId}/respond`, {
        items: responseData.items,
        attachments: responseData.attachments,
        message: responseData.message,
        clientName: responseData.clientName
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate messages queries to refetch
      queryClient.invalidateQueries({ queryKey: clientMessagesKeys.messages() });
    },
  });
}

/**
 * Get unread message count for notification badge
 */
export function useUnreadMessageCount() {
  return useQuery({
    queryKey: [...clientMessagesKeys.all, 'unread-count'],
    queryFn: async () => {
      const response = await apiClient.get('/messages/pending');
      return response?.messages?.length || 0;
    },
  });
}

export default {
  useClientMessages,
  useClientMessage,
  useRespondToMessage,
  useUnreadMessageCount,
};
